/**
 * auth domain (doc 01 A0.3). Mirrors `GET /auth/me`. The BFF owns the session;
 * this wrapper only reads the resolved principal used to shape navigation,
 * gated fetches, and reveal/trail affordances.
 *
 * NOTE: the working OIDC login/callback/logout wiring lives in the admin app
 * (`apps/admin/src/lib/auth.tsx`) and is intentionally not duplicated here.
 */
import { get, post } from './transport.js';
/**
 * THE CLIENT'S `can()`. A MIRROR — not a second opinion.
 *
 * The same rules as `can()` in apps/api/src/domains/auth/authorization.ts, which
 * is the authority and stays the authority. This exists so a screen can ask the
 * question the server is about to be asked BEFORE it fires a request that will
 * be refused, and before it writes a sentence about the organization it has no
 * grounds for. It can never grant anything: every route re-decides, so a client
 * that answers `true` here and meets a 403 has learned it was wrong, in the safe
 * direction.
 *
 * The rules, restated because a mirror that drifts silently is worse than no
 * mirror:
 *   - DEFAULT DENY. No grants, no matching grant, unreadable set — `false`.
 *   - A grant with `scopeType: 'org'` satisfies ANY requested scope.
 *   - Any other grant satisfies only a request naming the same `scopeType` AND
 *     the same non-null `scopeId`.
 *   - A query with no scope means the ORGANIZATION-WIDE requirement, matching
 *     the server's `ScopeQuery = { scopeType: 'org' }` default. It is the one
 *     most callers want and the one they must not get by accident, which is why
 *     it is the default on both sides rather than on only one.
 *
 * WHEN THE SERVER PREDATES `permissionScopes` this falls back to `permissions`,
 * so every grant reads as organization-wide — the pre-existing behaviour,
 * unchanged. A knowingly permissive fallback, and the right one: the alternative
 * is a newly-deployed admin bundle refusing an org_admin every screen until the
 * API rolls forward.
 */
export function can(me, permission, scope) {
    const grants = me?.permissionScopes;
    if (grants === undefined) {
        // Pre-`permissionScopes` server (or no answer yet). All there is to read is
        // the flattened list, which cannot distinguish scopes at all.
        return me?.permissions?.includes(permission) ?? false;
    }
    const want = scope ?? { scopeType: 'org' };
    for (const grant of grants) {
        if (grant.key !== permission)
            continue;
        if (grant.scopeType === 'org')
            return true;
        if (want.scopeType === grant.scopeType &&
            want.scopeId != null &&
            want.scopeId === grant.scopeId) {
            return true;
        }
    }
    return false;
}
/**
 * Does this principal hold `permission` AT ANY SCOPE?
 *
 * Mirrors `holdsAtAnyScope` on the server, INCLUDING the warning that comes with
 * it there: it deliberately ignores `scopeType` and `scopeId`, and reaching for
 * it because {@link can} refused somebody you expected it to allow is how the
 * flattening defect gets reintroduced. Reach over other people is never this
 * question.
 *
 * It has one correct use on the client that the server's version does not have,
 * and that use is why this is a named export rather than an inline `.includes()`:
 * REVEALING A SCREEN. The API answers an out-of-scope read two different ways
 * depending on the route — `programs` and the dashboard's dimensions NARROW to
 * what the grant covers and return 200, `directory/workers` and `supervision`
 * REFUSE with 403 — and nothing in this payload says which kind a given key is.
 * Gating a screen on the organization-wide question would tell a program-scoped
 * reader they have no access to a screen the server would serve them, narrowed.
 * So the gate stays permissive and the server decides, which is the only
 * division of labour that is right for both kinds of route.
 */
export function holdsAtAnyScope(me, permission) {
    const grants = me?.permissionScopes;
    if (grants === undefined)
        return me?.permissions?.includes(permission) ?? false;
    return grants.some((grant) => grant.key === permission);
}
/** `GET /auth/me` — throws {@link UnauthorizedError} on 401 (→ sign-in). */
export function me() {
    return get('/auth/me');
}
/** `POST /auth/switch-org` — switch the active org (requires an active session). */
export function switchOrg(orgId) {
    return post('/auth/switch-org', { orgId });
}
/** `POST /auth/select-org` — complete login org selection (uses the signed select cookie). */
export function selectOrg(orgId) {
    return post('/auth/select-org', { orgId });
}
//# sourceMappingURL=auth.js.map